import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mapStyle = { width: '100%', height: '300px', borderRadius: '12px' };
const defaultCenter = { lat: -13.9626, lng: 33.7741 }; // Lilongwe fallback

const STATUS_PILL = {
  awaiting_approval: 'bg-amber-100 text-amber-800',
  pending: 'bg-blue-100 text-blue-800',
  accepted: 'bg-indigo-100 text-indigo-800',
  in_progress: 'bg-green-100 text-green-800',
};

/**
 * "Live" tab: parents see all in-flight family rides on a map plus a list below.
 * Polls /api/family/{primary}/active-rides every 5 s.
 * Also surfaces any currently-active SOS alerts.
 */
const FamilyLiveTab = ({ primaryId }) => {
  const [rides, setRides] = useState([]);
  const [sosAlerts, setSosAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapRef, setMapRef] = useState(null);
  const didFitRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!primaryId) return;
    try {
      const [ridesRes, sosRes] = await Promise.all([
        fetch(`${API_URL}/api/family/${primaryId}/active-rides`).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/family/${primaryId}/sos?status=active`).then(r => r.json()).catch(() => null),
      ]);
      if (ridesRes?.success) setRides(ridesRes.items || []);
      if (sosRes?.success) setSosAlerts(sosRes.items || []);
    } finally {
      setLoading(false);
    }
  }, [primaryId]);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 5000);
    return () => clearInterval(t);
  }, [fetchData]);

  // Fit bounds to all markers the first time we get data
  useEffect(() => {
    if (!mapRef || didFitRef.current) return;
    const points = [];
    rides.forEach(r => {
      if (r.driver_location?.latitude && r.driver_location?.longitude) {
        points.push({ lat: r.driver_location.latitude, lng: r.driver_location.longitude });
      }
      if (r.pickup_location?.latitude && r.pickup_location?.longitude) {
        points.push({ lat: r.pickup_location.latitude, lng: r.pickup_location.longitude });
      }
      if (r.dropoff_location?.latitude && r.dropoff_location?.longitude) {
        points.push({ lat: r.dropoff_location.latitude, lng: r.dropoff_location.longitude });
      }
    });
    if (points.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach(p => bounds.extend(p));
      mapRef.fitBounds(bounds, 60);
      didFitRef.current = true;
    }
  }, [mapRef, rides]);

  const handleResolveSos = async (alertId) => {
    try {
      await fetch(`${API_URL}/api/family/${primaryId}/sos/${alertId}/resolve`, { method: 'POST' });
      fetchData();
    } catch (_) { /* ignore */ }
  };

  const markers = [];
  rides.forEach(r => {
    const name = r.member_name || 'Family';
    if (r.driver_location?.latitude && r.driver_location?.longitude) {
      markers.push({
        key: `drv-${r.ride_id}`,
        position: { lat: r.driver_location.latitude, lng: r.driver_location.longitude },
        label: { text: name[0]?.toUpperCase() || 'F', color: '#fff', fontSize: '12px', fontWeight: '700' },
        title: `${name} · driver`,
      });
    }
    if (r.dropoff_location?.latitude && r.dropoff_location?.longitude) {
      markers.push({
        key: `drp-${r.ride_id}`,
        position: { lat: r.dropoff_location.latitude, lng: r.dropoff_location.longitude },
        title: `${name} · destination`,
        opacity: 0.7,
      });
    }
  });

  return (
    <div data-testid="family-live-tab">
      {sosAlerts.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="family-sos-banner">
          {sosAlerts.map(a => (
            <div key={a.id} className="bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-xl p-3 flex items-start justify-between gap-2 animate-pulse">
              <div className="min-w-0">
                <p className="text-sm font-bold text-red-700 dark:text-red-300">🚨 SOS from {a.member_name || 'family member'}</p>
                <p className="text-xs text-red-700/80 dark:text-red-300/80">
                  {a.message || 'Emergency alert triggered.'}
                </p>
                {a.latitude && a.longitude && (
                  <a
                    href={`https://maps.google.com/?q=${a.latitude},${a.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] underline text-red-700 dark:text-red-300"
                    data-testid="sos-location-link"
                  >
                    Open location in Maps
                  </a>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleResolveSos(a.id)}
                data-testid="sos-resolve-btn"
                className="shrink-0 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold"
              >
                Mark resolved
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 mb-3" data-testid="family-live-map">
        <GoogleMap
          mapContainerStyle={mapStyle}
          center={markers[0]?.position || defaultCenter}
          zoom={12}
          onLoad={setMapRef}
          options={{ disableDefaultUI: true, zoomControl: true, clickableIcons: false }}
        >
          {markers.map(m => (
            <Marker key={m.key} position={m.position} label={m.label} title={m.title} opacity={m.opacity} />
          ))}
        </GoogleMap>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
        Active family rides {rides.length > 0 && <span className="text-gray-400">({rides.length})</span>}
      </h3>

      {loading ? (
        <p className="py-6 text-center text-xs text-gray-500">Loading…</p>
      ) : rides.length === 0 ? (
        <p className="py-6 text-center text-xs text-gray-500" data-testid="family-live-empty">
          No active rides right now. This view updates every 5 seconds.
        </p>
      ) : (
        <div className="space-y-2">
          {rides.map(r => (
            <div
              key={r.ride_id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3"
              data-testid="family-live-ride-row"
            >
              <div className="flex items-center justify-between mb-1 gap-2">
                <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{r.member_name || 'Family member'}</p>
                <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${STATUS_PILL[r.status] || 'bg-gray-200 text-gray-700'}`}>
                  {(r.status || '').replace('_', ' ')}
                </span>
              </div>
              <p className="text-xs text-gray-500 truncate">
                <span className="font-medium">From:</span> {r.pickup_location?.address || '—'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                <span className="font-medium">To:</span> {r.dropoff_location?.address || '—'}
              </p>
              {r.driver_name && (
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                  Driver: <span className="font-medium">{r.driver_name}</span>
                  {r.driver_phone && (
                    <a href={`tel:${r.driver_phone}`} className="ml-2 text-green-700 dark:text-green-400 underline" data-testid="family-live-call-driver">
                      Call
                    </a>
                  )}
                </p>
              )}
              {r.driver_location?.latitude && r.driver_location?.longitude && (
                <a
                  href={`https://maps.google.com/?q=${r.driver_location.latitude},${r.driver_location.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-blue-700 dark:text-blue-400 underline"
                >
                  Open driver location in Maps
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FamilyLiveTab;
